import type { HttpClient } from "@/core/http/types";
import type { ServerClip } from "./clipList";

/** Snake_case keys: sent as query params, which the backend never re-cases. */
export type VoiceTarget = Record<string, string>;

export interface CreateClipRequest {
  purpose: string;
  target: VoiceTarget;
  mimeType: string;
  sizeBytes: number;
  durationSeconds: number;
}

export interface CreatedClip {
  clipId: string;
  uploadUrl: string;
  /** The exact type the upload URL was signed for. Send it, never blob.type. */
  contentTypeHeader: string;
}

const CLIPS = "/api/voice/clips";

/** Portal tokens must name their client in the query string for require_auth. */
function scope(http: HttpClient): { params?: { client_id: string } } {
  return http.context.kind === "portal" ? { params: { client_id: http.context.clientId } } : {};
}

export const voiceService = {
  createClip(http: HttpClient, request: CreateClipRequest): Promise<CreatedClip> {
    return http.post<CreatedClip>(CLIPS, request, scope(http));
  },

  submitClip(http: HttpClient, clipId: string): Promise<ServerClip> {
    return http.post<ServerClip>(`${CLIPS}/${clipId}/submit`, undefined, scope(http));
  },

  retryClip(http: HttpClient, clipId: string): Promise<ServerClip> {
    return http.post<ServerClip>(`${CLIPS}/${clipId}/retry`, undefined, scope(http));
  },

  discardClip(http: HttpClient, clipId: string): Promise<void> {
    return http.delete<void>(`${CLIPS}/${clipId}`, undefined, scope(http));
  },

  listOpenClips(http: HttpClient, purpose: string, target: VoiceTarget): Promise<ServerClip[]> {
    return http.get<ServerClip[]>(CLIPS, { params: { purpose, ...target, ...scope(http).params } });
  },
};
